import {
  ListBox,
  ListBoxRow,
  Pressable,
  Text,
} from '@spotify-internal/encore-web';
import { Popover, PopoverRoot } from '@spotify-internal/encore-web/next';

import { ISSUE_KIND_OPTIONS, IssueKind, getIssueKindLabel } from '../../types';
import { IssueKindColoredIcon, IssueKindLabel } from './IssueKindLabel';

import styles from './IssueKindPicker.module.css';

export type IssueKindPickerProps = {
  disabled?: boolean;
  disableIcon?: boolean;
  value?: IssueKind | null;
  onChange: (kind: IssueKind) => void;
};

const IssueKindPickerOptions = ({
  close,
  value,
  onChange,
}: Pick<IssueKindPickerProps, 'value' | 'onChange'> & {
  close: () => void;
}) => {
  const handleSelectionChange = (keys: 'all' | Set<React.Key>): void => {
    if (keys === 'all') {
      return;
    }

    const [selectedKey] = keys;
    const selectedOption = ISSUE_KIND_OPTIONS.find(
      option => option.value === selectedKey,
    );

    if (!selectedOption) {
      return;
    }

    onChange(selectedOption.value);
    close();
  };

  return (
    <ListBox
      aria-label="Change issue kind"
      selectionMode="single"
      selectedKeys={value ? new Set([value]) : new Set()}
      onSelectionChange={handleSelectionChange}
    >
      {ISSUE_KIND_OPTIONS.map(option => (
        <ListBoxRow
          key={option.value}
          id={option.value}
          textValue={option.label}
          size="small"
          isDisabled={option.value === value}
        >
          <IssueKindLabel kind={option.value} />
        </ListBoxRow>
      ))}
    </ListBox>
  );
};

export const IssueKindPicker = ({
  disabled = false,
  disableIcon = false,
  value,
  onChange,
}: IssueKindPickerProps) => {
  const label = value ? getIssueKindLabel(value) : 'Unspecified';
  const trigger = (
    <button
      type="button"
      className={styles.trigger}
      disabled={disabled}
      aria-label={`Change issue kind, current value: ${label}`}
     data-track-source-anchor="263009e091;issue-kind-picker-button">
      {!disableIcon && <IssueKindColoredIcon kind={value} />}
      <Text as="span" variant="bodySmall">
        {label}
      </Text>
    </button>
  );

  if (disabled) {
    return trigger;
  }

  return (
    <PopoverRoot>
      <Pressable>{trigger}</Pressable>
      <Popover
        aria-label="Change issue kind"
        placement="bottomStart"
        hasNoArrow
        className={styles.popover}
      >
        {({ close }) => (
          <IssueKindPickerOptions
            value={value}
            onChange={onChange}
            close={close}
           data-track-source-anchor="5b8d7904d6;issue-kind-picker-issue-kind-picker-options"/>
        )}
      </Popover>
    </PopoverRoot>
  );
};
